<script setup lang="ts">
import { $getCampaignOptions } from '@/api/campaigns'

interface IProps {
  modelValue?: any,
  label?: string,
  isSelectAllVisible?: boolean,
  selectOptions?: any[] | null,
  /**
   * Optional array of category2 IDs that should be disabled for selection.
   * If null or undefined, no event groups will be disabled.
   */
  disabledCategories2?: number[] | null,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: null,
  label: 'Category 2',
  isSelectAllVisible: false,
  selectOptions: null,
  disabledCategories2: null,
})

interface CustomEvents {
  (e: 'update:modelValue', newValue: any): void
}

const emit = defineEmits<CustomEvents>()

interface Category2 {
  value: number
  display_name: string
}

const categories2 = ref<Category2[]>([])
const categories2Loading = ref<boolean>(false)

const localSearchStr = ref<string>('') // For client-side filtering

const category2Options = computed(() => {
  if (props.selectOptions) {
    return props.selectOptions
  } else {
    return categories2.value
  }
})

const filteredCategories2 = computed(() => {
  if (!localSearchStr.value) return category2Options.value
  const searchLower = localSearchStr.value.toLowerCase()
  return category2Options.value.filter((option: Category2) =>
    option.display_name?.toLowerCase().includes(searchLower)
  )
})

const selectedValues = computed(() => Array.isArray(props.modelValue) ? props.modelValue : [])

const allFilteredSelected = computed(() => {
  if (!filteredCategories2.value.length) return false
  return filteredCategories2.value.every((option: Category2) =>
    selectedValues.value.some((selected: Category2) => selected.value === option.value)
  )
})

const someFilteredSelected = computed(() => {
  if (!filteredCategories2.value.length) return false
  const selectedCount = filteredCategories2.value.filter((option: Category2) =>
    selectedValues.value.some((selected: Category2) => selected.value === option.value)
  ).length
  return selectedCount > 0 && selectedCount < filteredCategories2.value.length
})

const toggleSelectAll = () => {
  if (allFilteredSelected.value || someFilteredSelected.value) {
    const newValue = selectedValues.value.filter((selected: Category2) =>
      !filteredCategories2.value.some((option: Category2) => option.value === selected.value)
    )
    emit('update:modelValue', newValue)
  } else {
    const newValue = [...selectedValues.value]
    filteredCategories2.value.forEach((option: Category2) => {
      if (!newValue.some((selected: Category2) => selected.value === option.value)) {
        newValue.push(option)
      }
    })
    emit('update:modelValue', newValue)
  }
}

const getCategories2 = async () => {
  categories2Loading.value = true
  try {
    const res = await $getCampaignOptions()
    categories2.value = res.data.actions.POST.category_2.choices
  } catch (e) {
    console.log(e)
  } finally {
    categories2Loading.value = false
  }
}

const onFocusCategory2Select = async () => {
  if (!categories2.value.length && !props.selectOptions) {
    await getCategories2()
  }
}

// Handle client-side search for Select All functionality
const handleLocalSearch = (searchStr: string) => {
  localSearchStr.value = searchStr
}
</script>

<template>
  <VAutocomplete
    :model-value="modelValue"
    :items="[...filteredCategories2]"
    return-object
    item-title="display_name"
    item-value="value"
    :label="label"
    density="compact"
    multiple
    clearable
    @update:modelValue="newValue => $emit('update:modelValue', newValue)"
    @update:focused="onFocusCategory2Select"
    @update:search="val => localSearchStr = val"
  >
    <template #prepend-item>
      <VListItem
        v-if="isSelectAllVisible && filteredCategories2?.length > 1"
        @click="toggleSelectAll"
        class="v-list-item--select-all"
      >
        <template #prepend>
          <VCheckboxBtn
            :model-value="allFilteredSelected"
            :indeterminate="someFilteredSelected"
          />
        </template>
        <VListItemTitle>
          {{ allFilteredSelected || someFilteredSelected ? 'Deselect' : 'Select' }} All
          ({{ filteredCategories2.length }} found)
        </VListItemTitle>
      </VListItem>
      <VDivider v-if="isSelectAllVisible && filteredCategories2?.length > 1" />
    </template>

    <template
      v-if="disabledCategories2"
      #item="{ item, index, props }"
    >
      <VListItem
        v-bind="props"
        :disabled="!!disabledCategories2 ? disabledCategories2.includes(item?.value) : false"
      />
    </template>
    <template #selection="{ item, index }">
      <div class="d-flex align-center">
        <v-chip v-if="index < 1">
          <span>{{ item.title }}</span>
        </v-chip>
        <span
          v-if="index === 1"
          class="text-grey text-caption align-self-center"
        >
          (+{{ modelValue?.length - 1 }} others)
        </span>
      </div>
    </template>
    <template #no-data>
      <div
        v-if="!!categories2Loading"
        class="d-flex justify-center align-center"
      >
        Loading...
        <VProgressCircular
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </div>
      <div
        v-else
        class="d-flex justify-center align-center"
      >
        Categories not found
      </div>
    </template>
  </VAutocomplete>
</template>

<style lang="scss">

</style>
